<file-picker>
  <div class="file-picker-container w-full flex flex-col gap-4">
    <!-- Shared Drive Selection (for Google Drive only) -->
    <shared-drive-selector
      if="{props.provider === 'google_drive' && !state.driveSelected}"
      repositoryId="{props.repositoryId}"
      provider="{props.provider}"
      mode="{props.mode}"
      onDriveSelected="{(data) => handleDriveSelected(data)}" />

    <!-- SharePoint Site Selection (for SharePoint only) -->
    <sharepoint-site-selector
      if="{props.provider === 'sharepoint' && !state.driveSelected}"
      repositoryId="{props.repositoryId}"
      mode="{props.mode}"></sharepoint-site-selector>

    <!-- File Browsing Section (only show after drive is selected) -->
    <div if="{state.driveSelected}" class="file-browsing-section">
      <!-- Drive Selection Info & Change Drive Button (Google Drive only) -->
      <div if="{props.provider === 'google_drive'}" class="drive-info-bar bg-base-100 rounded-lg p-3 mb-4 flex items-center justify-between border border-base-300">
        <div class="flex items-center gap-3">
          <i class="icon-storage text-lg text-primary"></i>
          <div>
            <span class="text-xs text-gray-500 block">{locale?.t('filepicker_selected_drive')}</span>
            <span class="font-medium">{state.selectedDriveName || locale?.t('filepicker_my_drive')}</span>
          </div>
        </div>

        <button
          onclick="{changeDrive}"
          class="btn btn-sm btn-outline"
          disabled="{state.loading}">
          <i class="icon-metalark-edit me-1"></i>
          {locale?.t('filepicker_change_drive')}
        </button>
      </div>

      <!-- Site Selection Info & Change Site Button (SharePoint only) -->
      <div if="{props.provider === 'sharepoint'}" class="drive-info-bar bg-base-100 rounded-lg p-3 mb-4 flex items-center justify-between border border-base-300">
        <div class="flex items-center gap-3">
          <i class="icon-business text-lg text-info"></i>
          <div>
            <span class="text-xs text-gray-500 block">{locale?.t('filepicker_selected_site')}</span>
            <span class="font-medium">{state.selectedDriveName || locale?.t('filepicker_sharepoint_site')}</span>
          </div>
        </div>
        <button
          onclick="{changeDrive}"
          class="btn btn-sm btn-outline"
          disabled="{state.loading}">
          <i class="icon-metalark-edit me-1"></i>
          {locale?.t('filepicker_change_site')}
        </button>
      </div>

      <!-- Breadcrumb Navigation with Prefix Indicator -->
      <nav class="breadcrumbs text-sm overflow-x-auto">
        <ul class="flex gap-2">
          <!-- First breadcrumb item with optional prefix badge -->
          <li>
            <button
              type="button"
              onclick="{() => navigateToFolder(state.breadcrumbs[0].id)}"
              class="link link-hover text-primary cursor-pointer flex items-center gap-2">
              {state.breadcrumbs[0].name || locale?.t('filepicker_root')}
              <!-- Prefix Badge (for S3 only, show if prefix is defined and not empty) -->
              <span if="{props.provider === 's3' && state.s3Prefix && state.s3Prefix.length > 0}" class="badge badge-info badge-xs gap-0.5">
                <i class="icon-folder text-xs"></i>
                <span class="text-xs">{state.s3Prefix}</span>
              </span>
            </button>
          </li>
          <!-- Remaining breadcrumb items -->
          <li each="{breadcrumb in state.breadcrumbs.slice(1)}">
            <button
              type="button"
              onclick="{() => navigateToFolder(breadcrumb.id)}"
              class="link link-hover text-primary cursor-pointer">
              {breadcrumb.name}
            </button>
          </li>
        </ul>
      </nav>

      <!-- Toolbar -->
      <div class="flex gap-2 flex-wrap items-center">
        <input
          id="file-picker-search"
          type="search"
          placeholder="{locale?.t('filepicker_search_placeholder')}"
          aria-label="{locale?.t('filepicker_search_placeholder')}"
          class="input input-bordered input-sm flex-1 min-w-[200px]"
          value="{state.searchQuery}"
          oninput="{(e) => handleSearch(e.target.value)}"
          onsearch="{(e) => handleSearch(e.target.value)}" />
        
        <button
          onclick="{refreshFiles}"
          class="btn btn-sm btn-outline"
          disabled="{state.loading}">
          <i class="icon-refresh me-1"></i>
          {locale?.t('filepicker_refresh')}
        </button>
        
        <button
          if="{!isFolderMode() && !isSingleSelect()}"
          onclick="{selectAll}"
          class="btn btn-sm btn-outline"
          disabled="{state.loading || state.files.length === 0}">
          <i class="icon-check me-1"></i>
          {locale?.t('filepicker_all')}
        </button>
        
        <button
          onclick="{clearFilter}"
          class="btn btn-sm btn-outline"
          disabled="{state.loading || !state.searchQuery}">
          <i class="icon-clear me-1"></i>
          {locale?.t('filepicker_clear_filter')}
        </button>

        <button
          if="{!isFolderMode()}"
          onclick="{clearSelection}"
          class="btn btn-sm btn-outline"
          disabled="{state.selectedCount === 0 || state.loading}">
          <i class="icon-clear me-1"></i>
          {locale?.t('filepicker_clear_selection')}
        </button>
      </div>

      <!-- Search scope hint (provider-specific) -->
      <div class="text-xs text-base-content/80 mt-1 flex items-center gap-1">
        <i class="icon-info1"></i>
        <span>{locale?.t(getSearchScopeHintKey())}</span>
      </div>

      <!-- Loading State -->
      <div if="{state.loading}" class="flex justify-center py-12">
        <span class="loading loading-spinner loading-lg"></span>
      </div>

      <!-- Empty State -->
      <div if="{!isFolderMode() && !state.loading && state.displayedItems.length === 0 && !state.searchQuery}" class="card bg-base-100 shadow-md">
        <div class="card-body text-center py-12">
          <i class="icon-cloud text-6xl text-gray-300 mb-2"></i>
          <p class="text-gray-500">{locale?.t('filepicker_no_files_found')}</p>
        </div>
      </div>

      <!-- No Search Results -->
      <div if="{!isFolderMode() && !state.loading && state.displayedItems.length === 0 && state.searchQuery}" class="card bg-base-100 shadow-md">
        <div class="card-body text-center py-12">
          <i class="icon-search text-6xl text-gray-300 mb-2"></i>
          <p class="text-gray-500">{locale?.t('filepicker_no_search_results', { query: state.searchQuery })}</p>
        </div>
      </div>

      <!-- File Tree -->
      <div if="{!isFolderMode() && !state.loading && state.displayedItems.length > 0}" class="file-tree overflow-y-auto border border-base-300 rounded-lg bg-base-50 max-h-96 my-4">
        <!-- Folders Section -->
        <div if="{state.folders.length > 0}" class="folders-section">
          <div class="sticky top-0 px-4 py-2 bg-base-200 border-b border-base-300 z-10">
            <h4 class="text-xs font-semibold text-base-content/80 uppercase">{locale?.t('filepicker_folders_count', { count: state.folders.length })}</h4>
          </div>
          <div each="{folder in state.folders}" class="file-item folder px-4 py-3 hover:bg-base-100 border-b border-base-300 last:border-b-0 cursor-pointer transition-colors">
            <div class="flex items-center gap-3 w-full">
              <i class="icon-folder text-lg text-amber-500"></i>
              <button
                type="button"
                onclick="{() => openFolder(folder)}"
                class="link link-hover flex-1 text-start text-sm font-medium">
                {folder.name}
              </button>
              <span class="text-xs text-gray-500 whitespace-nowrap">{formatSize(folder.size)}</span>
            </div>
          </div>
        </div>

        <!-- Files Section -->
        <div if="{state.files.length > 0}" class="files-section">
          <div if="{state.folders.length > 0}" class="sticky px-4 py-2 bg-base-200 border-b border-base-300 z-10">
            <h4 class="text-xs font-semibold text-base-content/80 uppercase">{locale?.t('filepicker_files_count', { count: state.files.length })}</h4>
          </div>
          <div each="{file in state.files}" class="file-item px-4 py-3 hover:bg-base-100 border-b border-base-300 last:border-b-0 transition-colors">
            <div class="flex items-center gap-3 w-full">
              <input
                id="file-picker-select-{file.id}"
                type="checkbox"
                class="checkbox checkbox-sm cursor-pointer"
                aria-label="{file.name}"
                checked="{file.selected}"
                onchange="{() => toggleFile(file)}" />
              <i class="icon-{getFileIcon(file.extension)} text-lg text-info"></i>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-sm truncate block flex-1" title="{file.name}">
                    {file.name}
                  </span>
                  <!-- Open this file where it lives, on the repository's own site -->
                  <a
                    if="{file.previewLink}"
                    href="{file.previewLink}"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs link link-primary no-underline hover:underline"
                    title="{previewTooltip()}"
                    aria-label="{previewTooltip()}">
                    <i class="icon-link"></i>
                  </a>
                </div>
                <span class="text-xs text-gray-500">
                  {formatSize(file.size)} • {tools?.formatDate(file.modifiedAt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading More Indicator -->
        <div if="{state.loadingMore}" class="loading-more-section flex justify-center py-4 border-t border-base-300">
          <div class="flex items-center gap-2">
            <span class="loading loading-spinner loading-sm"></span>
            <span class="text-sm text-base-content/80">{locale?.t('filepicker_loading_more')}</span>
          </div>
        </div>
      </div>

      <!-- Folder Tree (folder selection mode) -->
      <div if="{isFolderMode() && !state.loading}" class="file-tree overflow-y-auto border border-base-300 rounded-lg bg-base-50 max-h-96 my-4">
        <div class="file-item folder px-4 py-3 hover:bg-base-100 border-b border-base-300">
          <label class="flex items-center gap-3 w-full cursor-pointer">
            <input
              type="radio"
              name="folder-pick"
              class="radio radio-sm radio-primary"
              checked="{state.selectedFolderId === '__current__'}"
              onchange="{selectCurrentFolder}"
              aria-label="{currentFolderSelectLabel()}" />
            <i class="icon-folder-open text-lg text-primary" aria-hidden="true"></i>
            <span class="flex-1 text-sm font-medium">{currentFolderSelectLabel()}</span>
          </label>
        </div>

        <div if="{state.folders.length > 0}" class="sticky top-0 px-4 py-2 bg-base-200 border-b border-base-300 z-10">
          <h4 class="text-xs font-semibold text-base-content/80 uppercase">{locale?.t('filepicker_folders_count', { count: state.folders.length })}</h4>
        </div>

        <div each="{folder in state.folders}" class="file-item folder px-4 py-3 hover:bg-base-100 border-b border-base-300 last:border-b-0 transition-colors">
          <div class="flex items-center gap-3 w-full">
            <input
              type="radio"
              name="folder-pick"
              class="radio radio-sm radio-primary cursor-pointer"
              id="folder-pick-{folder.id}"
              checked="{state.selectedFolderId === folder.id}"
              onchange="{() => selectFolder(folder)}"
              aria-label="{folder.name}" />
            <i class="icon-folder text-lg text-amber-500" aria-hidden="true"></i>
            <button
              type="button"
              onclick="{() => openFolder(folder)}"
              class="link link-hover flex-1 text-start text-sm font-medium">
              {folder.name}
            </button>
            <i class="icon-metalark-chevron-right text-base-content/40" aria-hidden="true"></i>
          </div>
        </div>

        <div if="{state.folders.length === 0 && !state.searchQuery}" class="px-4 py-6 text-center text-xs text-base-content/70">{locale?.t('filepicker_no_subfolders')}</div>
        <div if="{state.folders.length === 0 && state.searchQuery}" class="px-4 py-6 text-center text-xs text-base-content/70">{locale?.t('filepicker_no_search_results', { query: state.searchQuery })}</div>

        <div if="{state.loadingMore}" class="loading-more-section flex justify-center py-4 border-t border-base-300">
          <div class="flex items-center gap-2">
            <span class="loading loading-spinner loading-sm"></span>
            <span class="text-sm text-base-content/80">{locale?.t('filepicker_loading_more')}</span>
          </div>
        </div>
      </div>

      <!-- Selection Summary & Actions -->
      <div if="{!isFolderMode() && !state.loading}" class="card bg-base-200 shadow-md border border-base-300">
        <div class="card-body py-4 px-6">
          <div class="flex items-center justify-between gap-4">
            <div class="flex gap-6">
              <div>
                <span class="text-xs text-gray-500 block mb-1">{isSingleSelect() ? locale?.t('filepicker_selected_file') : locale?.t('filepicker_selected_files')}</span>
                <span class="font-bold text-2xl">{tools?.formatNumber(state.selectedCount)}</span>
              </div>
              <div class="divider divider-horizontal m-0"></div>
              <div>
                <span class="text-xs text-gray-500 block mb-1">{locale?.t('filepicker_total_size')}</span>
                <span class="font-bold text-lg">{formatSize(state.selectedSize)}</span>
              </div>
            </div>
            <button
              onclick="{confirmSelection}"
              class="btn btn-primary btn-sm"
              disabled="{state.selectedCount === 0}">
              <i class="icon-arrow_forward me-1"></i>
              {importButtonLabel()}
            </button>
          </div>
        </div>
      </div>

      <!-- OAuth Reauthorization Modal -->
      <div if="{state.showOAuthModal}" class="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="file-picker-oauth-modal-heading">
        <div class="modal-box">
          <h3 id="file-picker-oauth-modal-heading" class="font-bold text-lg mb-4">
            <i class="icon-warning text-warning me-2"></i>
            {locale?.t('filepicker_connection_expired')}
          </h3>
          <p class="py-4">{state.oauthErrorMessage}</p>
          <div class="modal-action gap-2">
            <button
              onclick="{closeOAuthModal}"
              aria-label="{locale?.t('button_cancel')}"
              class="btn btn-ghost">
              {locale?.t('button_cancel')}
            </button>
            <button
              onclick="{reauthorizeRepository}"
              aria-label="{locale?.t('filepicker_reauthorize_now')}"
              class="btn btn-primary">
              <i class="icon-refresh me-1"></i>
              {locale?.t('filepicker_reauthorize_now')}
            </button>
          </div>
        </div>
        <button type="button" aria-label="{locale?.t('close')}" class="modal-backdrop" onclick="{closeOAuthModal}"></button>
      </div>

    </div>
  </div>

  <script>
    import { getRepositoryService } from '../../../services/service-loader';
    import { getProviderDisplayName } from '../../../constants/RepositoryProviderConstants';
    import { values } from '../../../services/values';
    import sharedDriveSelector from './shared-drive-selector.tag';
    import sharepointSiteSelector from '../../sharepoint-site-selector.tag';

    export default {

      get components() {
        return { sharedDriveSelector, sharepointSiteSelector };
      },

      props: {
        repositoryId: null,
        provider: null,
        onFilesSelected: null,
        onFolderSelected: null,
        mode: 'file'
      },

      state: {
        driveSelected: false,
        selectedDriveName: null,
        breadcrumbs: [{ id: 'root', name: null }],
        currentFolderId: 'root',
        folders: [],
        files: [],
        displayedItems: [],
        selectedCount: 0,
        selectedSize: 0,
        loading: false,
        loadingMore: false,
        searchQuery: '',
        hasMore: false,
        nextPageToken: null,
        allSelectedIds: new Set(),
        showOAuthModal: false,
        oauthErrorMessage: '',
        // Provider-specific context for file discovery
        sharedDriveId: null, // Root folder ID for shared drives (null for My Drive)
        siteId: null,
        driveId: null,
        currentDriveType: 'personal',
        // S3 prefix for scoped browsing
        s3Prefix: null,
        selectedFolderId: null,
        selectedFolder: null
      },

      async onMounted() {
        console.log('[File Picker] Component mounted with repositoryId:', this.props.repositoryId);

        // Riot doesn't deep-clone the component's `state` object between
        // instances, so mutable defaults declared on the component (notably
        // `allSelectedIds: new Set()` and `breadcrumbs: [...]`) are shared
        // across mounts. Without this reset, a Set populated by a previous
        // modal session leaks into the next one and inflates selectedCount.
        this.resetState();

        // Store the current repository ID to detect changes
        this._currentRepositoryId = this.props.repositoryId;

        // Initialize search debounce timer
        this._searchDebounceTimer = null;

        // Initial setup
        await this.initializeForRepository();
      },

      onBeforeUnmount() {
        // Clean up scroll listener
        this.removeScrollListener();
        this.removeSiteSelectionListener();

        // Clean up search debounce timer
        if (this._searchDebounceTimer) {
          clearTimeout(this._searchDebounceTimer);
          this._searchDebounceTimer = null;
        }
      },

      async onUpdated() {
        // Check if repository ID has changed
        if (this.props.repositoryId !== this._currentRepositoryId) {
          console.log('[File Picker] Repository changed from', this._currentRepositoryId, 'to', this.props.repositoryId, '- resetting state');
          this._currentRepositoryId = this.props.repositoryId;
          this.resetState();
          await this.initializeForRepository();
        }

        // Always ensure scroll listener is properly attached when file tree exists
        this.ensureScrollListener();
      },

      resetState() {
        // Clean up scroll listener before resetting
        this.removeScrollListener();

        // Reset all file browsing state to avoid caching issues
        this.update({
          driveSelected: false,
          selectedDriveName: null,
          breadcrumbs: [{ id: 'root', name: null }],
          currentFolderId: 'root',
          folders: [],
          files: [],
          displayedItems: [],
          selectedCount: 0,
          selectedSize: 0,
          loading: false,
          loadingMore: false,
          searchQuery: '',
          hasMore: false,
          nextPageToken: null,
          allSelectedIds: new Set(),
          sharedDriveId: null,
          siteId: null,
          driveId: null,
          currentDriveType: 'personal',
          s3Prefix: null,
          selectedFolderId: null,
          selectedFolder: null
        });
        console.log('[File Picker] State reset complete');
      },

      async initializeForRepository() {
        if (this.props.repositoryId) {
          console.log('[File Picker] Initializing for repository:', this.props.repositoryId, 'provider:', this.props.provider);
          
          // For Google Drive and SharePoint, wait for site/drive selection
          if (this.props.provider === 'google_drive' || this.props.provider === 'sharepoint') {
            this.update({ driveSelected: false });
            // Register site selection listener ONLY for SharePoint
            if (this.props.provider === 'sharepoint') {
              this.registerSiteSelectionListener();
              // FIX: Load repository config to pre-populate driveId if already configured
              await this.loadRepositoryConfig();
            }
          } else {
            // For other providers (S3, etc.), load config first then proceed to file browsing
            this.update({ driveSelected: true });
            await this.loadRepositoryConfig();
            await this.loadFolder('root');
          }
        }
      },

      async loadRepositoryConfig() {
        try {
          const repositoryService = await getRepositoryService();
          const response = await repositoryService.getById(this.props.repositoryId);
          
          console.log('[File Picker] Repository response:', response);
          console.log('[File Picker] Provider:', this.props.provider);
          
          // Handle both direct response and wrapped response
          const repository = response?.repository || response;
          
          // Always reset provider-specific state to avoid bleeding between repositories
          let stateUpdate = {
            driveId: null,
            siteId: null,
            s3Prefix: null,  // Reset S3 prefix for non-S3 repositories
            selectedDriveName: null  // Reset drive name
          };
          
          if (repository?.parameters) {
            const params = typeof repository.parameters === 'string'
              ? JSON.parse(repository.parameters)
              : repository.parameters;
            
            console.log('[File Picker] Loaded repository config:', params);
            console.log('[File Picker] Prefix value:', params.prefix);
            
            // Pre-populate provider context from repository config
            if (params.driveId) {
              stateUpdate.driveId = params.driveId;
              stateUpdate.siteId = params.siteId || null;
              stateUpdate.selectedDriveName = params.siteName || (params.siteId === 'personal' ? this.locale?.t('filepicker_my_onedrive') : this.locale?.t('filepicker_sharepoint_site'));
              console.log('[File Picker] Pre-populated driveId from config:', params.driveId);
            }
            
            // Extract S3 prefix if present (only for S3 provider)
            if (this.props.provider === 's3' && params.prefix !== undefined && params.prefix !== null) {
              stateUpdate.s3Prefix = params.prefix;
              console.log('[File Picker] ✅ Pre-populated S3 prefix from config:', params.prefix || '(empty)');
            } else if (this.props.provider === 's3') {
              console.log('[File Picker] No prefix found in parameters for S3 repository');
            }
          } else {
            console.log('[File Picker] No parameters found in repository response');
          }
          
          this.update(stateUpdate);
        } catch (error) {
          console.warn('[File Picker] Failed to load repository config:', error);
        }
      },

      async handleDriveSelected(data) {
        // Called when user selects a Shared Drive
        console.log('Drive selected:', data);

        // Store the drive name and ID for UI display and backend context
        // data.driveId is the root folder ID from shared-drive-selector (null for My Drive, folder ID for shared drives)
        this.update({
          driveSelected: true,
          selectedDriveName: data.name || data.displayName || data.driveName || this.locale?.t('filepicker_my_drive'),
          sharedDriveId: data.driveId || null,
          currentDriveType: data.driveId ? 'shared' : 'personal',
        });

        // Reload files from the selected drive
        await this.loadFolder('root');
      },

      registerSiteSelectionListener() {
        this.removeSiteSelectionListener();
        const self = this;

        this._handleSiteSelected = (data) => {
          const payload = data.detail || data;
          const { siteId, driveId, selectedSite } = payload;
          console.log('SharePoint site selected:', siteId, selectedSite);
          self.update({
            driveSelected: true,
            selectedDriveName: selectedSite.displayName || selectedSite.name || self.locale?.t('filepicker_sharepoint_site'),
            siteId: siteId,
            driveId: driveId || selectedSite.driveId,
            currentDriveType: siteId === 'personal' ? 'personal' : 'team',
          });
          self.loadFolder('root');
        };

        this._handleSiteError = (data) => {
          const payload = data.detail || data;
          const { error } = payload;
          console.error('SharePoint site selection failed:', error);
          alert(self.locale.t('alert_sharepoint_select_failed') + ': ' + (error?.message || 'Unknown error'));
        };

        if (this.$root && this.$root.$on) {
          this.$root.$on('site-selected', this._handleSiteSelected);
          this.$root.$on('site-error', this._handleSiteError);
        }
        window.addEventListener('sharepoint-site-selected', this._handleSiteSelected);
        window.addEventListener('sharepoint-site-error', this._handleSiteError);
      },

      removeSiteSelectionListener() {
        if (this.$root && this.$root.$off) {
          if (this._handleSiteSelected) this.$root.$off('site-selected', this._handleSiteSelected);
          if (this._handleSiteError) this.$root.$off('site-error', this._handleSiteError);
        }
        if (this._handleSiteSelected) window.removeEventListener('sharepoint-site-selected', this._handleSiteSelected);
        if (this._handleSiteError) window.removeEventListener('sharepoint-site-error', this._handleSiteError);
        this._handleSiteSelected = null;
        this._handleSiteError = null;
      },

      changeDrive() {
        // Clean up scroll listener before resetting
        this.removeScrollListener();

        // Reset drive selection to show the shared-drive-selector again
        this.update({
          driveSelected: false,
          selectedDriveName: null,
          searchQuery: '',
          breadcrumbs: [{ id: 'root', name: null }],
          currentFolderId: 'root',
          folders: [],
          files: [],
          displayedItems: [],
          selectedCount: 0,
          selectedSize: 0,
          loadingMore: false,
          hasMore: false,
          nextPageToken: null,
          allSelectedIds: new Set(),
          // Reset provider context
          sharedDriveId: null,
          siteId: null,
          driveId: null,
          currentDriveType: 'personal',
          selectedFolderId: null,
          selectedFolder: null
        });
        if (this.isFolderMode() && this.props.onFolderSelected) {
          this.props.onFolderSelected(null);
        }
      },

      async loadFolder(folderId, preserveBreadcrumbs = false, clearSearch = true) {
        if (!this.props.repositoryId) return;

        try {
          // Clear search only when navigating to a new folder (not when refreshing or searching)
          if (clearSearch) {
            this.update({ loading: true, searchQuery: '' });
          } else {
            this.update({ loading: true });
          }

          // Update breadcrumbs only if not preserving existing ones
          let breadcrumbs;
          if (preserveBreadcrumbs) {
            // Keep existing breadcrumbs during refresh
            breadcrumbs = this.state.breadcrumbs;
          } else {
            // Rebuild breadcrumbs for navigation

            breadcrumbs = [{ id: 'root', name: null }];
            if (folderId !== 'root') {
              const folder = this.state.folders.find(f => f.id === folderId);
              if (folder) {
                breadcrumbs.push({ id: folderId, name: folder.name });
              }
            }
          }
          this.update({ breadcrumbs, currentFolderId: folderId });

          // For shared drives, when navigating to 'root', use the actual root folder ID
          const apiFolderId = (folderId === 'root' && this.state.sharedDriveId)
            ? this.state.sharedDriveId
            : folderId;

          // Fetch files from API with current search query
          const response = await this.fetchFilesFromApi(apiFolderId, null, this.state.searchQuery || null);
          
          const folders = response.folders || [];
          const files = response.files || [];

          // Add selection state to files
          const filesWithState = files.map(f => ({
            ...f,
            selected: this.state.allSelectedIds.has(f.id)
          }));

          // When searching, API returns filtered results directly - no client-side filtering needed
          this.update({
            folders,
            files: filesWithState,
            displayedItems: [...folders, ...filesWithState],
            hasMore: response.hasMore || false,
            nextPageToken: response.nextPageToken,
            loading: false
          });

          // Ensure scroll listener is attached after loading files
          this.ensureScrollListener();

          if (this.isFolderMode()) {
            this.selectCurrentFolder();
          }

          // Check if content doesn't fill the container and we have more to load
          setTimeout(() => {
            const fileTree = this.$('.file-tree');
            if (fileTree && this.state.hasMore && fileTree.scrollHeight <= fileTree.clientHeight) {
              console.log('[File Picker] Content does not fill container, auto-loading more');
              this.loadMore();
            }
          }, 100);
        } catch (error) {
          console.error('Failed to load folder:', error);
          this.update({ loading: false });
          
          // Check if it's an OAuth token expiration error
          if (error.code === 'oauth_token_expired' || error.message?.includes('oauth_token_expired')) {
            // Show OAuth re-auth modal with custom modal system
            this.showOAuthExpiredModal(error);
          } else {
            alert(this.locale.t('alert_sharepoint_load_files_failed') + ': ' + (error.message || 'Unknown error'));
          }
        }
      },
 
      ensureScrollListener() {
        // Always clean up existing listener first to avoid duplicates
        this.removeScrollListener();

        // Find the file tree element and attach scroll listener
        const fileTree = this.$('.file-tree');
        if (fileTree && this.state.hasMore) {
          this._scrollHandler = () => this.handleScroll();
          this._fileTreeElement = fileTree;
          fileTree.addEventListener('scroll', this._scrollHandler, { passive: true });
          console.log('[File Picker] Scroll listener attached, hasMore:', this.state.hasMore);
        }
      },

      removeScrollListener() {
        if (this._fileTreeElement && this._scrollHandler) {
          this._fileTreeElement.removeEventListener('scroll', this._scrollHandler);
          this._fileTreeElement = null;
          this._scrollHandler = null;
          console.log('[File Picker] Scroll listener removed');
        }
      },

      handleScroll() {
        const fileTree = this._fileTreeElement || this.$('.file-tree');

        // Don't load more if: loading, already loading more, or no more to load
        // Note: Pagination works with search now (server-side search supports pagination)
        if (!fileTree || this.state.loading || this.state.loadingMore || !this.state.hasMore) {
          return;
        }

        // Check if scrolled near bottom (with 200px threshold for better UX)
        const scrollTop = fileTree.scrollTop;
        const scrollHeight = fileTree.scrollHeight;
        const clientHeight = fileTree.clientHeight;
        const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

        console.log('[File Picker] Scroll detected - distance from bottom:', distanceFromBottom, 'hasMore:', this.state.hasMore);

        if (distanceFromBottom <= 200) {
          console.log('[File Picker] Triggering loadMore()');
          this.loadMore();
        }
      },

      async loadMore() {
        if (this.state.loadingMore || !this.state.hasMore || !this.state.nextPageToken) {
          return;
        }

        try {
          this.update({ loadingMore: true });

          // For shared drives, when at root, use the actual root folder ID
          const currentFolderId = this.state.currentFolderId;
          const apiFolderId = (currentFolderId === 'root' && this.state.sharedDriveId)
            ? this.state.sharedDriveId
            : currentFolderId;

          // Fetch more files from API with current search query
          const response = await this.fetchFilesFromApi(apiFolderId, this.state.nextPageToken, this.state.searchQuery || null);

          const newFolders = response.folders || [];
          const newFiles = response.files || [];

          // Add selection state to new files
          const newFilesWithState = newFiles.map(f => ({
            ...f,
            selected: this.state.allSelectedIds.has(f.id)
          }));

          // Deduplicate: only add folders/files that aren't already in the list (based on id)
          const existingFolderIds = new Set(this.state.folders.map(f => f.id));
          const existingFileIds = new Set(this.state.files.map(f => f.id));

          const uniqueNewFolders = newFolders.filter(f => !existingFolderIds.has(f.id));
          const uniqueNewFiles = newFilesWithState.filter(f => !existingFileIds.has(f.id));

          // Append only unique items to existing lists
          const folders = [...this.state.folders, ...uniqueNewFolders];
          const files = [...this.state.files, ...uniqueNewFiles];

          this.update({
            folders,
            files,
            displayedItems: [...folders, ...files],
            hasMore: response.hasMore || false,
            nextPageToken: response.nextPageToken,
            loadingMore: false
          });

          // Re-establish scroll listener with updated hasMore state
          this.ensureScrollListener();
        } catch (error) {
          console.error('Failed to load more files:', error);
          this.update({ loadingMore: false });
          // Don't show alert for lazy load errors, just log them
        }
      },

      async fetchFilesFromApi(folderId, pageToken = null, search = null) {
        try {
          // Use the repository service's browse API
          const repositoryService = await getRepositoryService();

          const response = await repositoryService.browseFiles(
            this.props.repositoryId,
            folderId,
            pageToken,
            search
          );
          
          console.log('Browse API response:', response);
          
          // Check for OAuth token expiration response (from 401 that was converted to response)
          if (response?.error === 'oauth_token_expired' || response?.needsReauth) {
            const error = new Error(response.message || 'OAuth tokens expired or invalid');
            error.code = 'oauth_token_expired';
            error.response = response;
            throw error;
          }
          
          return response || {
            folders: [],
            files: [],
            hasMore: false
          };
        } catch (error) {
          console.error('Failed to fetch files from API:', error);
          // Re-throw OAuth expiration errors so they're handled by loadFolder
          if (error.code === 'oauth_token_expired') {
            throw error;
          }
          // Check if error response contains oauth_token_expired (from axios 401 with response body)
          if (error.response?.data?.error === 'oauth_token_expired') {
            const oauthError = new Error(error.response.data.message || 'OAuth tokens expired or invalid');
            oauthError.code = 'oauth_token_expired';
            oauthError.response = error.response.data;
            throw oauthError;
          }
          // Return empty state on other errors instead of throwing
          return {
            folders: [],
            files: [],
            hasMore: false,
            error: error.message
          };
        }
      },

      navigateToFolder(folderId) {
        if (folderId === this.state.currentFolderId) return;

        // Find the clicked breadcrumb and trim the breadcrumbs array to that point
        const clickedIndex = this.state.breadcrumbs.findIndex(bc => bc.id === folderId);
        if (clickedIndex !== -1) {
          const trimmedBreadcrumbs = this.state.breadcrumbs.slice(0, clickedIndex + 1);
          this.update({ breadcrumbs: trimmedBreadcrumbs });
        }

        // Clear search when navigating to a different folder
        this.loadFolder(folderId, true, true);
      },

      async openFolder(folder) {
        const newBreadcrumbs = [...this.state.breadcrumbs, { id: folder.id, name: folder.name }];
        this.update({ breadcrumbs: newBreadcrumbs });
        // Clear search when opening a new folder
        await this.loadFolder(folder.id, true, true);
      },

      previewTooltip() {
        // Names the repository the link actually leads to. This used to read "Preview in
        // Google Drive or online viewer" for every provider, which was wrong everywhere
        // but Drive — a Moodle file offered to open in Google Drive.
        return this.locale?.t('filepicker_preview_tooltip', {
          provider: getProviderDisplayName(this.props.provider),
        });
      },

      getSearchScopeHintKey() {
        // Returns the locale key describing search scope for the current provider.
        // Behavior matches each provider's native search:
        // - S3 + SharePoint search recursively from the current folder
        // - Google Drive only searches direct children of the current folder
        switch (this.props.provider) {
          case 'google_drive':
            return 'filepicker_search_scope_current_folder_only';
          case 'sharepoint':
          case 's3':
          default:
            return 'filepicker_search_scope_recursive';
        }
      },

      handleSearch(query) {
        // Update search query immediately for UI feedback
        this.update({ searchQuery: query });

        // Clear existing debounce timer
        if (this._searchDebounceTimer) {
          clearTimeout(this._searchDebounceTimer);
        }

        // Debounce the actual search API call
        this._searchDebounceTimer = setTimeout(async () => {
          // Trigger server-side search by reloading current folder with search query
          // clearSearch=false preserves the search query we just set
          await this.loadFolder(this.state.currentFolderId, true, false);
        }, values.debounceTime);
      },

      async clearFilter() {
        this.update({ searchQuery: '' });
        // Reload folder without search query (clearSearch=true to ensure it's cleared)
        await this.loadFolder(this.state.currentFolderId, true, true);
      },

      async refreshFiles() {
        // Preserve search query when refreshing (clearSearch=false)
        await this.loadFolder(this.state.currentFolderId, true, false);
      },

      // Single-select mode (maxfiles=1, e.g. reupload) allows exactly one file:
      // picking one clears any prior selection.
      isSingleSelect() {
        return Number(this.props.maxfiles) === 1;
      },

      // Import button label. Singular in single-select mode; otherwise the
      // count-aware plural (or the bare label when nothing is selected).
      importButtonLabel() {
        if (this.isSingleSelect()) {
          return this.locale?.t('filepicker_import_file') || 'Import File';
        }
        return this.state.selectedCount > 0
          ? this.locale?.t('filepicker_import_files_count', { count: this.state.selectedCount })
          : this.locale?.t('filepicker_import_files');
      },

      toggleFile(file) {
        const selected = !file.selected;

        // In single-select mode, choosing a file replaces the previous one.
        if (selected && this.isSingleSelect()) {
          this.state.files.forEach(f => { f.selected = false; });
          this.state.allSelectedIds.clear();
        }

        file.selected = selected;

        // Update selection tracking using externalFileId (provider-specific ID used for discovery)
        const fileId = file.externalFileId || file.id;
        if (selected) {
          this.state.allSelectedIds.add(fileId);
        } else {
          this.state.allSelectedIds.delete(fileId);
        }

        // Update counts
        const selectedCount = this.state.allSelectedIds.size;
        const selectedSize = this.state.files
          .filter(f => f.selected)
          .reduce((total, f) => total + (f.size || 0), 0);

        this.update({ 
          selectedCount,
          selectedSize
        });
      },

      selectAll() {
        this.state.files.forEach(file => {
          file.selected = true;
          // FIX: Use consistent ID logic (same as toggleFile)
          const fileId = file.externalFileId || file.id;
          this.state.allSelectedIds.add(fileId);
        });

        const selectedCount = this.state.allSelectedIds.size;
        const selectedSize = this.state.files
          .reduce((total, f) => total + (f.size || 0), 0);

        this.update({ selectedCount, selectedSize });
      },

      clearSelection() {
        this.state.files.forEach(file => {
          file.selected = false;
        });
        this.state.allSelectedIds.clear();

        this.update({ 
          selectedCount: 0,
          selectedSize: 0
        });
      },

      async confirmSelection() {
        const selectedFileIds = Array.from(this.state.allSelectedIds);
        
        if (selectedFileIds.length === 0) {
          alert(this.locale.t('alert_sharepoint_select_file_required'));
          return;
        }

        if (this.props.onFilesSelected) {
          // Build provider context, excluding null/undefined values
          const providerContext = {
            provider: this.props.provider,
          };
          
          // Only include non-null values
          if (this.state.sharedDriveId) providerContext.sharedDriveId = this.state.sharedDriveId;
          if (this.state.siteId) providerContext.siteId = this.state.siteId;
          if (this.state.driveId) providerContext.driveId = this.state.driveId;
          if (this.state.currentDriveType) providerContext.driveType = this.state.currentDriveType;
          
          console.log('[File Picker] confirmSelection - Provider Context:', {
            provider: this.props.provider,
            sharedDriveId: this.state.sharedDriveId,
            siteId: this.state.siteId,
            driveId: this.state.driveId,
            driveType: this.state.currentDriveType,
            contextToSend: providerContext
          });
          
          // Validate required fields for SharePoint
          if (this.props.provider === 'sharepoint' && !this.state.driveId) {
            alert(this.locale.t('alert_sharepoint_drive_id_missing'));
            return;
          }
          
          this.props.onFilesSelected({
            fileIds: selectedFileIds,
            count: selectedFileIds.length,
            totalSize: this.state.selectedSize,
            providerContext: providerContext
          });
        }
      },

      isFolderMode() {
        return this.props.mode === 'folder';
      },

      buildProviderContext() {
        const ctx = { provider: this.props.provider };
        if (this.state.sharedDriveId) ctx.sharedDriveId = this.state.sharedDriveId;
        if (this.state.siteId) ctx.siteId = this.state.siteId;
        if (this.state.driveId) ctx.driveId = this.state.driveId;
        if (this.state.currentDriveType) ctx.driveType = this.state.currentDriveType;
        return ctx;
      },

      currentFolderSelectLabel() {
        const segs = this.state.breadcrumbs.filter(b => b.name).map(b => b.name);
        const here = segs.length ? segs[segs.length - 1] : (this.state.selectedDriveName || this.locale?.t('filepicker_root'));
        return this.locale?.t('filepicker_use_current_folder', { name: here });
      },

      buildFolderEmit(folder) {
        const driveName = this.state.selectedDriveName || undefined;
        const driveId = this.state.sharedDriveId || this.state.driveId || undefined;
        const segs = this.state.breadcrumbs.filter(b => b.name).map(b => b.name);
        const providerContext = this.buildProviderContext();
        const isS3 = this.props.provider === 's3';
        if (folder) {
          return {
            folderId: folder.id,
            name: folder.name,
            path: isS3 ? folder.id : [...segs, folder.name].join('/'),
            breadcrumb: [driveName, ...segs, folder.name].filter(Boolean),
            driveId,
            driveName,
            providerContext
          };
        }
        const isRoot = this.state.currentFolderId === 'root';
        const curId = (isRoot && this.state.sharedDriveId) ? this.state.sharedDriveId : this.state.currentFolderId;
        const name = segs.length ? segs[segs.length - 1] : (driveName || this.locale?.t('filepicker_root'));
        return {
          folderId: (isS3 && isRoot) ? (this.state.s3Prefix || '') : curId,
          name,
          path: isS3 ? (isRoot ? (this.state.s3Prefix || '') : this.state.currentFolderId) : segs.join('/'),
          breadcrumb: [driveName, ...segs].filter(Boolean),
          driveId,
          driveName,
          providerContext
        };
      },

      selectCurrentFolder() {
        const sel = this.buildFolderEmit(null);
        this.update({ selectedFolderId: '__current__', selectedFolder: sel });
        if (this.props.onFolderSelected) this.props.onFolderSelected(sel);
      },

      selectFolder(folder) {
        const sel = this.buildFolderEmit(folder);
        this.update({ selectedFolderId: folder.id, selectedFolder: sel });
        if (this.props.onFolderSelected) this.props.onFolderSelected(sel);
      },

      getFileIcon(extension) {
        const iconMap = {
          'pdf': 'file-pdf',
          'doc': 'file-word',
          'docx': 'file-word',
          'xls': 'file-excel',
          'xlsx': 'file-excel',
          'ppt': 'file-openoffice',
          'pptx': 'file-openoffice',
          'txt': 'file-text',
          'vtt': 'file-text',
          'md': 'file-text',
          'csv': 'file-excel',
          'zip': 'archive',
          'jpg': 'file-picture',
          'jpeg': 'file-picture',
          'png': 'file-picture',
          'gif': 'file-picture',
          'video': 'file-video',
          'mp4': 'file-video',
          'mov': 'file-video'
        };
        return iconMap[extension?.toLowerCase()] || 'file-openoffice';
      },

      formatSize(bytes) {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
      },

      showOAuthExpiredModal(error) {
        // Create a custom modal for OAuth re-authentication
        this.update({
          showOAuthModal: true,
          oauthErrorMessage: error.message || 'Your repository connection has expired. Please re-authorize to continue browsing files.'
        });
      },

      async reauthorizeRepository() {
        try {
          // Get OAuth URL
          const repositoryService = await getRepositoryService();
          const oauthData = await repositoryService.getOAuthUrl(this.props.repositoryId);
          
          if (oauthData?.authorizationUrl) {
            // Create a promise that resolves when OAuth completes
            const oauthPromise = new Promise((resolve) => {
              const messageHandler = (event) => {
                // Verify message is from our origin
                if (event.origin !== window.location.origin) return;
                
                // Check for OAuth completion message
                if (event.data?.type === 'repository-connected') {
                  window.removeEventListener('message', messageHandler);
                  resolve(event.data);
                }
              };
              
              // Listen for postMessage from OAuth callback popup
              window.addEventListener('message', messageHandler);
              
              // Timeout after 5 minutes if no response
              setTimeout(() => {
                window.removeEventListener('message', messageHandler);
                resolve({ success: false, timeout: true });
              }, 5 * 60 * 1000);
            });
            
            // Open OAuth popup
            window.open(oauthData.authorizationUrl, 'oauth-popup', 'width=500,height=600');
            
            // Wait for OAuth callback
            const result = await oauthPromise;
            
            // Close modal and reload
            this.update({ showOAuthModal: false });
            
            if (result.success !== false) {
              // OAuth succeeded, reload folder to get fresh tokens (preserve breadcrumbs)
              await this.loadFolder(this.state.currentFolderId, true);
            } else if (result.timeout) {
              alert(this.locale.t('alert_oauth_timeout'));
            } else {
              alert(this.locale.t('alert_oauth_failed') + ': ' + (result.error || 'Unknown error'));
            }
          } else {
            alert(this.locale.t('alert_oauth_url_failed'));
          }
        } catch (error) {
          console.error('Failed to re-authorize:', error);
          alert(this.locale.t('alert_oauth_reauth_failed') + ': ' + (error.message || 'Unknown error'));
        }
      },

      closeOAuthModal() {
        this.update({ showOAuthModal: false });
      },

    }
  </script>

  <style>
    :scope {
      --file-picker-max-height: 500px;
    }

    .file-picker-container {
      font-family: inherit;
    }

    .drive-info-bar {
      background: linear-gradient(to right, #f8f9fa 0%, #ffffff 100%);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .drive-info-bar .btn {
      min-width: 140px;
    }


    .breadcrumbs {
      flex-wrap: wrap;
      gap: 0.25rem;
    }

    .file-tree {
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    }

    .file-tree::-webkit-scrollbar {
      width: 6px;
    }

    .file-tree::-webkit-scrollbar-track {
      background: transparent;
    }

    .file-tree::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 3px;
    }

    .file-item {
      transition: background-color 0.2s ease;
    }

    .file-item.folder {
      font-weight: 500;
    }
  </style>
</file-picker>
